import { extent } from 'd3-array';
import { Axis, axisBottom, axisRight } from 'd3-axis';
import { ScaleLinear, ScaleTime, scaleLinear, scaleTime } from 'd3-scale';
import { Selection, select } from 'd3-selection';
import { Area, CurveFactory, Line, area, curveLinear, line } from 'd3-shape';
import { timeFormat } from 'd3-time-format';

export interface DataPoint {
  [key: string]: Date | number;
  date: Date;
  allocation: number;
}

interface AllocationOverTimeGraphSettings {
  width: number;
  height: number;
  graphHeight: number;
  margin: {
    right: number;
    left: number;
    top: number;
    bottom: number;
  };
  element: SVGElement;
  allocationByDate: DataPoint[];
  interpolation: CurveFactory;
}

type AllocationOverTimeGraphDefaults = Pick<
  AllocationOverTimeGraphSettings,
  'margin' | 'graphHeight' | 'interpolation'
>;

export type AllocationOverTimeGraphOptions = Partial<AllocationOverTimeGraphDefaults> &
  Omit<AllocationOverTimeGraphSettings, 'element' | 'width' | 'height' | keyof AllocationOverTimeGraphDefaults>;

interface AllocationOverTimeGraphGenerators {
  xAxis: Axis<Date>;
  yAxis: Axis<number>;
  line: {
    [key: string]: Line<DataPoint>;
    allocation: Line<DataPoint>;
  };
  area: {
    [key: string]: Area<DataPoint>;
    stock: Area<DataPoint>;
    bond: Area<DataPoint>;
  };
}

export default class AllocationOverTimeGraph {
  settings: AllocationOverTimeGraphSettings;

  scales: { x: ScaleTime<number, number>; y: ScaleLinear<number, number> };

  svg: Selection<SVGGElement, DataPoint, HTMLElement, unknown>;

  generators: AllocationOverTimeGraphGenerators;

  constructor(element: SVGElement, options: AllocationOverTimeGraphOptions) {
    const DEFAULTS: AllocationOverTimeGraphDefaults = {
      margin: {
        top: 20,
        right: 70,
        left: 50,
        bottom: 50,
      },
      graphHeight: Math.min(0.75 * element.parentElement!.clientWidth, 375),
      interpolation: curveLinear,
    };

    this.settings = { ...DEFAULTS, ...options, element, width: 0, height: 0 };
    this.settings.width =
      this.settings.element.parentElement!.clientWidth - this.settings.margin.left - this.settings.margin.right;
    this.settings.height = this.settings.graphHeight - this.settings.margin.top - this.settings.margin.bottom;
  }

  draw() {
    this.emptyLayout();
    this.setUpScales();
    this.setUpGenerators();
    this.createLayout();
    this.drawData();
  }

  emptyLayout() {
    const svg = this.settings.element;
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }
  }

  setUpScales() {
    this.scales = {
      x: scaleTime()
        .domain(
          extent<DataPoint, Date>(this.settings.allocationByDate, d => {
            return d.date;
          }) as [Date, Date]
        )
        .range([0, this.settings.width]),
      y: scaleLinear().domain([0, 100]).range([this.settings.height, 0]).nice(),
    };
  }

  setUpGenerators() {
    const { scales } = this;
    const { interpolation } = this.settings;
    const axisDateFormat = timeFormat('%Y');
    const xTicks = 3;
    const yTicks = 5;

    this.generators = {
      xAxis: axisBottom<Date>(scales.x).ticks(xTicks).tickPadding(8).tickFormat(axisDateFormat),
      yAxis: axisRight<number>(scales.y)
        .ticks(yTicks)
        .tickFormat(d => {
          return `${d}%`;
        }),
      line: {
        allocation: line<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y(d => scales.y(d.allocation) as number)
          .curve(interpolation),
      },
      area: {
        stock: area<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y0(() => scales.y(0) as number)
          .y1(d => scales.y(d.allocation) as number)
          .curve(interpolation),

        bond: area<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y0(d => scales.y(d.allocation) as number)
          .y1(() => scales.y(100) as number)
          .curve(interpolation),
      },
    };
  }

  createLayout() {
    this.svg = select<SVGElement, DataPoint>('svg.AllocationOverTimeGraph-svg')
      .attr('width', this.settings.width + this.settings.margin.left + this.settings.margin.right)
      .attr('height', this.settings.height + this.settings.margin.top + this.settings.margin.bottom)
      .append('g')
      .attr('transform', `translate(${this.settings.margin.left},${this.settings.margin.top})`);

    this.svg
      .append('g')
      .attr('class', 'AllocationOverTimeGraph-axis')
      .attr('transform', `translate(0,${this.settings.height})`)
      .call(this.generators.xAxis);

    this.svg
      .append('g')
      .attr('class', 'AllocationOverTimeGraph-axis')
      .attr('transform', `translate(${this.settings.width}, 0 )`)
      .call(this.generators.yAxis);

    this.svg.selectAll('g.tick').attr('class', 'AllocationOverTimeGraph-axisTick AllocationOverTimeGraph-label');
  }

  drawData() {
    this.drawArea('bond');
    this.drawArea('stock');
    this.drawLine('allocation');
    this.drawLabels();
  }

  drawArea(type: string) {
    this.svg
      .append('path')
      .datum(this.settings.allocationByDate)
      .attr('class', `AllocationOverTimeGraph-area AllocationOverTimeGraph-area--${type}`)
      .attr('d', this.generators.area[type]);
  }

  drawLine(type: string) {
    this.svg
      .append('path')
      .datum(this.settings.allocationByDate)
      .attr('class', `AllocationOverTimeGraph-line AllocationOverTimeGraph-line--${type}`)
      .attr('d', this.generators.line[type]);
  }

  drawLabels() {
    const initialAllocation = this.settings.allocationByDate[0].allocation;

    this.svg
      .append('text')
      .attr('class', 'AllocationOverTimeGraph-label')
      .attr('text-anchor', 'end')
      .attr('x', -5)
      .attr('y', (this.scales.y(initialAllocation) as number) - 20)
      .text('Bonds');

    this.svg
      .append('text')
      .attr('class', 'AllocationOverTimeGraph-label')
      .attr('text-anchor', 'end')
      .attr('x', -5)
      .attr('y', (this.scales.y(initialAllocation) as number) + 20)
      .text('Stocks');
  }
}
